/**
 * Boundary module for `Instant` values — absolute UTC moments (ISO 8601).
 *
 * Unlike `PlannedDate` (floating, never converted), an `Instant` IS converted to
 * the viewer's timezone before display. This is the canonical formatter the audit
 * flagged as missing: it applies the user's `dateFormat` + `timeFormat` tokens AND
 * the user's `timezone` together, so Instant fields stop falling back to browser
 * locale (`toLocaleDateString`) or a hardcoded shape (`extractLocalDateTime`).
 *
 * Use this for audit timestamps, time-entries, timed calendar events and any
 * `date-time` contract field OUTSIDE tasks. Do NOT use it for tasks' `PlannedDate`
 * (use `formatPlannedDate`), and never treat analytics `date-time` as a
 * `PlannedDateSnapshot`.
 */

import { format } from 'date-fns';

import type { DateFormat, TimeFormat } from '@/core/stores/ui.store';

export interface FormatInstantOptions {
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  timezone: string;
  /** Append the time component (` ` + `timeFormat`). Defaults to false. */
  showTime?: boolean;
}

/**
 * Build a faux-local `Date` carrying the instant's wall-clock components as read
 * in `timeZone`. Formatting this with `date-fns` (which uses the runtime's local
 * zone) then yields the correct user-timezone rendering without a timezone lib.
 */
function zonedAnchor(iso: string, timeZone: string): Date | null {
  const instant = new Date(iso);
  if (isNaN(instant.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(instant);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);

  let hour = get('hour');
  if (hour === 24) hour = 0; // some engines emit "24" for midnight

  return new Date(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'), 0);
}

/** Format a UTC `Instant` in the user's timezone using their date/time tokens. */
export function formatInstant(iso: string | null | undefined, opts: FormatInstantOptions): string {
  if (!iso) return '';
  const anchor = zonedAnchor(iso, opts.timezone);
  if (!anchor) return '';

  const datePart = format(anchor, opts.dateFormat);
  if (!opts.showTime) return datePart;
  return `${datePart} ${format(anchor, opts.timeFormat)}`;
}

/** Date-only convenience: user timezone + `dateFormat`, no time. */
export function formatInstantDate(
  iso: string | null | undefined,
  opts: Omit<FormatInstantOptions, 'showTime'>,
): string {
  return formatInstant(iso, { ...opts, showTime: false });
}

/** Time-only convenience: user timezone + `timeFormat`. */
export function formatInstantTime(
  iso: string | null | undefined,
  opts: { timeFormat: TimeFormat; timezone: string },
): string {
  if (!iso) return '';
  const anchor = zonedAnchor(iso, opts.timezone);
  if (!anchor) return '';
  return format(anchor, opts.timeFormat);
}

/**
 * Parse a date-picker string value into a local `Date` for display/selection.
 *
 * A bare `yyyy-MM-dd` is anchored at **noon** so no timezone offset can roll it to
 * the adjacent day (the date-only off-by-one). A full ISO instant is parsed as-is.
 */
export function parseDatePickerValue(value: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
}

/** Offset (ms) of `timeZone` at the instant `utcMs`: `zoneWallClock - utc`. */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcMs));

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);

  let hour = get('hour');
  if (hour === 24) hour = 0;

  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
  );
  return asUtc - utcMs;
}

/**
 * Inverse of the read path: a floating wall-clock (`date` `yyyy-MM-dd`, `time` `HH:mm`)
 * interpreted **in `timeZone`** → the absolute UTC `Instant` (ISO string).
 *
 * Native two-pass offset resolution (no timezone library): treat the components as UTC to
 * get a provisional instant, subtract the zone offset there, then re-resolve the offset at
 * the candidate instant to correct DST-boundary cases. Use this to serialize a date+time a
 * user edited in their configured timezone — NOT the browser offset (`buildUtcIso`).
 */
export function zonedToInstant(date: string, time: string, timeZone: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const wallAsUtc = Date.UTC(y, m - 1, d, hh, mm, 0);

  const offset = zoneOffsetMs(wallAsUtc, timeZone);
  let utc = wallAsUtc - offset;
  const offset2 = zoneOffsetMs(utc, timeZone);
  if (offset2 !== offset) {
    utc = wallAsUtc - offset2;
  }

  return new Date(utc).toISOString();
}

/**
 * Read path for editable instants: the wall-clock `{ date: 'yyyy-MM-dd', time: 'HH:mm' }`
 * of a UTC `Instant` as seen in `timeZone`. Inverse of {@link zonedToInstant}. Returns
 * `null` for empty/invalid input.
 */
export function instantToZonedParts(
  iso: string | null | undefined,
  timeZone: string,
): { date: string; time: string } | null {
  if (!iso) return null;
  const anchor = zonedAnchor(iso, timeZone);
  if (!anchor) return null;
  return { date: format(anchor, 'yyyy-MM-dd'), time: format(anchor, 'HH:mm') };
}
